/**
 * Segments Service
 *
 * Handles Segments (contact groupings for campaign targeting).
 * Wraps the lists service with segment-named functions for UI consistency.
 *
 * Note: Database tables are named 'lists' for backwards compatibility.
 */

import type { UUID } from "../types/common.types";
import type { Contact } from "../types/contact.types";
import type {
  Segment,
  SegmentWithContacts,
  CreateSegmentInput,
  UpdateSegmentInput,
  QueryCriteria,
} from "../types/segment.types";

// Import underlying list service functions
import {
  getLists,
  getListById,
  createList,
  updateList,
  deleteList,
  getListWithContacts,
  executeListQuery,
  previewListQueryCount,
  addContactsToList,
  removeContactsFromList,
  getContactLists,
  getStaticLists,
  isContactInList,
} from "./lists.service";

// =====================================================
// SEGMENT CRUD OPERATIONS
// =====================================================

/**
 * Get all segments
 */
export async function getSegments(
  organizationId?: UUID,
  options: { includeArchived?: boolean } = {},
): Promise<Segment[]> {
  return getLists(organizationId, options);
}

/**
 * Get a single segment by ID
 */
export async function getSegmentById(id: UUID): Promise<Segment | null> {
  return getListById(id);
}

/**
 * Create a new segment
 */
export async function createSegment(
  input: CreateSegmentInput,
  userId: UUID,
): Promise<Segment> {
  return createList(input, userId);
}

/**
 * Update an existing segment
 */
export async function updateSegment(
  id: UUID,
  updates: UpdateSegmentInput,
  userId: UUID,
): Promise<Segment> {
  return updateList(id, updates, userId);
}

/**
 * Delete a segment
 */
export async function deleteSegment(id: UUID): Promise<void> {
  return deleteList(id);
}

/**
 * Get segment with contacts
 */
export async function getSegmentWithContacts(
  id: UUID,
): Promise<SegmentWithContacts | null> {
  return getListWithContacts(id);
}

// =====================================================
// QUERY EXECUTION
// =====================================================

/**
 * Execute query criteria to get matching contacts
 */
export async function executeSegmentQuery(
  criteria: QueryCriteria,
  organizationId?: UUID,
): Promise<Contact[]> {
  return executeListQuery(criteria, organizationId);
}

/**
 * Preview segment query results (count only)
 */
export async function previewSegmentQueryCount(
  criteria: QueryCriteria,
  organizationId?: UUID,
): Promise<number> {
  return previewListQueryCount(criteria, organizationId);
}

// =====================================================
// STATIC SEGMENT CONTACT MANAGEMENT
// =====================================================

/**
 * Add contacts to a static segment
 */
export async function addContactsToSegment(
  segmentId: UUID,
  contactIds: UUID[],
  userId?: UUID,
): Promise<void> {
  return addContactsToList(segmentId, contactIds, userId);
}

/**
 * Remove contacts from a static segment
 */
export async function removeContactsFromSegment(
  segmentId: UUID,
  contactIds: UUID[],
): Promise<void> {
  return removeContactsFromList(segmentId, contactIds);
}

/**
 * Get all segments (static + dynamic) that a contact currently belongs to.
 *
 * Static membership comes from the list_contacts join; dynamic membership
 * is evaluated against each segment's saved query_criteria, scoped to the
 * segment's organization_id when set. Returned in a single round-trip via
 * the `get_contact_segment_memberships` RPC.
 */
export async function getContactSegments(contactId: UUID): Promise<Segment[]> {
  return getContactLists(contactId);
}

/**
 * Get all static segments (for adding contacts to)
 */
export async function getStaticSegments(): Promise<Segment[]> {
  return getStaticLists();
}

/**
 * Check if a contact is in a specific segment
 */
export async function isContactInSegment(
  segmentId: UUID,
  contactId: UUID,
): Promise<boolean> {
  return isContactInList(segmentId, contactId);
}

// =====================================================
// TRIGGER CONFIGURATION (Phase 3)
// =====================================================

// Import trigger-related functions from lists service
import {
  getListsWithTriggers,
  getListsWithTriggerInfo,
  updateListTrigger,
  getListVoucherIssuances,
  hasVoucherBeenIssued,
  manuallyEvaluateList,
} from "./lists.service";

// Re-export with segment naming
export {
  getListsWithTriggers as getSegmentsWithTriggers,
  getListsWithTriggerInfo as getSegmentsWithTriggerInfo,
  updateListTrigger as updateSegmentTrigger,
  getListVoucherIssuances as getSegmentVoucherIssuances,
  hasVoucherBeenIssued as hasSegmentVoucherBeenIssued,
  manuallyEvaluateList as manuallyEvaluateSegment,
};
